import React from "react";
import { Users, Target, TrendingUp, MapPin, Check, Award, Shield, Heart } from "lucide-react";

const AboutSection = () => {
    return (
        <section id="nosotros" className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
            {/* Elementos decorativos de fondo */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-100 rounded-full opacity-20"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-100 rounded-full opacity-20"></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 relative z-10">
                {/* Header mejorado con imagen */}
                <div className="text-center mb-20">
                    <div className="mb-12">
                        <img
                            src="https://firebasestorage.googleapis.com/v0/b/xfinance-12e10.firebasestorage.app/o/Headers%2FMiguel%20mx23243%20copy.jpg?alt=media&token=01256693-c357-4f72-b464-1e6a0e9d32de"
                            alt="Miguel MX - Asesoría Financiera"
                            className="mx-auto rounded-3xl shadow-2xl max-w-3xl w-full h-auto transform hover:scale-105 transition-transform duration-700"
                        />
                    </div>
                    <div className="inline-block bg-gradient-to-r from-[#14840c] to-green-600 text-transparent bg-clip-text mb-8">
                        <h2 className="text-5xl lg:text-7xl font-black leading-tight">
                            Miguelmx
                        </h2>
                    </div>
                    <h3 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-8 leading-tight">
                        Asesoría financiera hecha por y para mexicanos
                    </h3>
                    <p className="text-xl text-slate-600 max-w-5xl mx-auto font-medium leading-relaxed">
                        En MiguelMX creemos que cada decisión financiera —personal o empresarial— debe tomarse con información clara, estrategia y confianza. Por eso existimos: para acercar asesoría financiera profesional en México, sin importar si estás comenzando a construir tu patrimonio o si diriges una empresa en crecimiento.
                    </p>
                </div>

                {/* Sección principal mejorada */}
                <div className="grid lg:grid-cols-2 gap-20 items-start mb-16">
                    {/* Filosofía expandida */}
                    <div className="space-y-10">
                        <div>
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 bg-gradient-to-r from-[#14840c] to-green-600 rounded-xl flex items-center justify-center mr-4">
                                    <Heart className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-4xl font-bold text-slate-900">
                                    Nuestra Filosofía
                                </h3>
                            </div>
                            <div className="space-y-8">
                                {[
                                    {
                                        icon: Users,
                                        title: "Centrados en el cliente",
                                        desc: "Entendemos primero tus necesidades, luego diseñamos la estrategia personalizada que te llevará al éxito financiero."
                                    },
                                    {
                                        icon: Target,
                                        title: "Objetividad total",
                                        desc: "No estamos casados con un producto específico, sino con tu bienestar financiero y el cumplimiento de tus metas."
                                    },
                                    {
                                        icon: TrendingUp,
                                        title: "Crecimiento con propósito",
                                        desc: "No promovemos el consumo por impulso, sino la toma de decisiones inteligentes con visión a largo plazo."
                                    }
                                ].map((item, index) => (
                                    <div key={index} className="group flex items-start space-x-5 p-6 rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300">
                                        <div className="w-14 h-14 bg-gradient-to-r from-green-100 to-green-200 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                                            <item.icon className="w-7 h-7 text-[#14840c]" />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h4>
                                            <p className="text-slate-600 font-medium leading-relaxed">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Tarjeta principal mejorada */}
                    <div className="bg-gradient-to-br from-[#14840c] via-green-600 to-green-700 p-10 rounded-3xl text-white shadow-2xl relative overflow-hidden">
                        {/* Elementos decorativos */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>

                        <div className="relative z-10">
                            <div className="text-center mb-10">
                                <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-8">
                                    <MapPin className="w-12 h-12 text-white" />
                                </div>
                                <h3 className="text-4xl font-bold mb-4">
                                    100% Mexicana
                                </h3>
                                <p className="text-green-100 text-xl font-medium">
                                    Nacida en Sonora con presencia nacional
                                </p>
                            </div>

                            <div className="mb-8 p-6 bg-white/10 backdrop-blur-sm rounded-2xl">
                                <p className="text-green-50 font-medium text-lg leading-relaxed">
                                    Somos una firma 100% mexicana conformada por especialistas e instituciones de inversiones, seguros, crédito y planificación financiera integral. Nuestro propósito es democratizar el acceso a herramientas financieras de alta calidad, y acompañar a nuestros clientes en sus metas con ética, conocimiento y cercanía.
                                </p>
                            </div>

                            <div className="mb-8">
                                <h4 className="text-2xl font-bold mb-6 text-white flex items-center">
                                    <Award className="w-6 h-6 mr-3" />
                                    ¿Por qué elegirnos?
                                </h4>
                                <div className="space-y-5">
                                    {[
                                        "Trabajamos con instituciones financieras validadas y reguladas en México y en el mundo.",
                                        "Ofrecemos soluciones reales, desde seguros personales hasta consultoría financiera tipo CFO para empresas.",
                                        "No creemos en la venta, sino en el acompañamiento estratégico a largo plazo."
                                    ].map((item, index) => (
                                        <div key={index} className="flex items-start space-x-4 group">
                                            <div className="w-6 h-6 bg-green-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <Check className="w-4 h-4 text-[#14840c]" />
                                            </div>
                                            <span className="text-green-50 font-medium text-lg leading-relaxed">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="pt-8 border-t border-green-400/30">
                                <div className="text-center">
                                    <Shield className="w-8 h-8 mx-auto mb-4 text-green-200" />
                                    <p className="text-xl font-bold text-white mb-2">
                                        Asesoría financiera independiente, responsable y cercana
                                    </p>
                                    <p className="text-green-200 text-lg font-medium">
                                        Desde Sonora, para todo México
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Nueva sección de estadísticas/valores */}
                <div className="grid md:grid-cols-3 gap-8 mt-20">
                    {[
                        {
                            icon: Shield,
                            title: "Confianza",
                            desc: "Instituciones reguladas y validadas",
                            color: "from-blue-500 to-blue-600"
                        },
                        {
                            icon: Users,
                            title: "Experiencia",
                            desc: "Especialistas en finanzas mexicanas",
                            color: "from-[#14840c] to-green-600"
                        },
                        {
                            icon: Award,
                            title: "Excelencia",
                            desc: "Soluciones de alta calidad",
                            color: "from-amber-500 to-orange-600"
                        }
                    ].map((item, index) => (
                        <div key={index} className="group text-center p-8 rounded-2xl hover:bg-white hover:shadow-xl transition-all duration-300">
                            <div className={`w-16 h-16 bg-gradient-to-r ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                <item.icon className="w-8 h-8 text-white" />
                            </div>
                            <h4 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h4>
                            <p className="text-slate-600 font-medium text-lg">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AboutSection;